import { create } from "zustand";
import {
  createAssistantConversation,
  deleteAPI,
  fetchAssistantSuggestions,
  getAssistantConversation,
  listAssistantConversations,
  streamAssistantMessage,
  type AssistantConversationMessage,
  type AssistantSuggestion,
  type AssistantSuggestionOrigin,
  type AssistantToolEvent,
} from "../../../api/frameworksApi";
import { API_URL } from "../../../constants";
import { useAuthStore, onSessionReset } from "../../../stores/authStore";
import { useUiContextStore } from "../../../stores/uiContextStore";
import type { ChatMessage, ConversationSummary, HistoryContext } from "./types";
import { ENABLE_TYPING_PREVIEW } from "./lib/typingPreview";
import { isBackendSourced, mapSuggestions } from "./lib/suggestions";
import { buildGreeting, type Greeting } from "./lib/greeting";

export interface AssistantProgress {
  iteration: number;
  lastTool?: string;
  toolHistory: AssistantToolEvent[];
}

interface ComplianceAssistantState {
  open: boolean;
  /**
   * Whether the panel has been opened at all this session.
   *
   * Read by `MainLayout` to decide whether to warm the suggestions for the page the user is on. It has
   * to be a separate flag from {@link open}: the point of prefetching is to have the answer ready
   * *before* the panel opens, and gating on `open` would be gating on the very moment it is too late.
   *
   * The gate exists because generation costs a model call. A user who never touches the assistant
   * should never pay for one; a user who has opened it once has said they want it, and every page
   * after that is warm by the time they click.
   */
  hasEverOpened: boolean;
  minimized: boolean;
  messages: ChatMessage[];
  isThinking: boolean;
  progress: AssistantProgress | null;
  conversationId: string | null;
  conversationLoading: boolean;
  conversationError: string | null;
  historyContext: HistoryContext | null;
  conversations: ConversationSummary[];
  historyLoading: boolean;
  historyListError: string | null;
  pinnedIds: string[];
  historyError: string | null;

  /**
   * The page the user is on right now, tracked for as long as the panel is open.
   *
   * Deliberately separate from {@link suggestionsRoute}: that one records which page the *displayed
   * suggestions* belong to and stops changing once a conversation starts, because suggestions are an
   * empty-state concern. This one must keep up regardless, since every chat request needs the live page.
   * Conflating the two is what made the assistant answer against a page the user had already left.
   */
  currentRoute: string | null;

  /**
   * The empty-state heading, chosen when that state appears rather than computed while it is on screen.
   *
   * Stored precisely so it holds still: re-renders, typing, resizing and the hour ticking over must not
   * change the words under the user's eyes. It advances only at the two moments the empty state can appear
   * — see `openPanel` and `resetConversation`.
   */
  greeting: Greeting | null;

  /**
   * How many empty states this session has shown.
   *
   * Drives two things from one number, so they change together rather than drifting: which greeting is
   * shown, and which window of each screen's question pool the backend rotates to. "Every time I open it,
   * show me something different" is one counter, not two.
   */
  emptyStateVisit: number;

  /** Panel-open suggestions. Empty when the backend had none, alongside a reason saying why. */
  suggestions: AssistantSuggestion[];
  suggestionsLoading: boolean;
  /** null while unloaded, or when the local fallback is showing rather than backend wording. */
  suggestionsSource: "BASE" | "ENRICHED" | null;
  /** Set when the caller may not use the current page's module; shown above the discovery suggestions. */
  suggestionsUnavailableReason: string | null;
  /**
   * The route these were loaded for, so navigating refetches and re-rendering the same page does not.
   *
   * Null while page-awareness is off: that state asks the application-wide question, so there is no page
   * to record — and it staying null across navigation is what holds the general set on screen.
   */
  suggestionsRoute: string | null;
  /**
   * The record these were loaded for.
   *
   * Part of the freshness check alongside the route, and the reason opening a record now refreshes the
   * panel: the OSCAL authoring pages keep selection in local state and leave the URL on the list path, so
   * route equality alone reported "already loaded" and nothing refetched.
   */
  suggestionsItemId: string | null;
  /**
   * The view these were loaded for.
   *
   * The same reasoning as `suggestionsItemId`, one level deeper: moving from a catalog's header into its
   * control-groups tree changes neither the route nor the selected record, so without this the freshness
   * check reports "already loaded" and the panel keeps the questions from the view the user has left.
   */
  suggestionsView: string | null;
  /**
   * What the page called itself when these were generated.
   *
   * Captured like the route and the view, for the same reason: it describes the rows on screen, and a
   * label read live would name the page the user has since reached above the previous page's questions.
   *
   * In the freshness check too, because it also reaches the model. The role dashboard's title comes from
   * the auth store, so on a cold load it can settle from "Dashboard" to "Administrator Dashboard" after
   * the first request — and questions written from the first name are questions in the wrong vocabulary.
   */
  suggestionsPageTitle: string | null;
  /** The rotation these were loaded for, so a new visit on the same page refetches. */
  suggestionsVariant: number | null;
  /**
   * Whether suggestions follow the page.
   *
   * On (the default) they refresh as the user navigates. Off they hold still, staying about the
   * record they were generated for — which is what a user chasing one application across several
   * screens wants. Not a switch that turns the feature off: with nothing loaded yet the first set is
   * still fetched, because there is nothing to hold.
   */
  pageAwareSuggestions: boolean;

  openPanel: () => void;
  hidePanel: () => void;
  closePanel: () => void;
  minimizePanel: () => void;
  maximizePanel: () => void;
  resetConversation: () => void;
  /** Clears every trace of the signed-in user's session. Called on logout. */
  resetSession: () => void;
  loadConversations: () => Promise<void>;
  openConversation: (id: string) => Promise<boolean>;
  enterHistoryConversation: (context: HistoryContext, messages: ChatMessage[]) => void;
  togglePin: (id: string) => void;
  deleteConversation: (id: string) => Promise<void>;
  clearHistoryError: () => void;
  /**
   * Record the page the user is on. Pure setter — deliberately triggers no fetch, so it stays safe to
   * call on every navigation while an answer is streaming.
   */
  setCurrentRoute: (route: string | null) => void;
  /**
   * Load suggestions for a UI path. Fire-and-forget and never rejects: a failure falls back to the
   * hardcoded starters rather than surfacing an error, because the panel opening is not worth failing
   * over. Skipped once a conversation has messages — suggestions are an empty-state concern.
   */
  loadSuggestions: (route: string | null) => Promise<void>;
  setPageAwareSuggestions: (enabled: boolean) => void;
  sendMessage: (text: string, origin?: AssistantSuggestionOrigin | null) => Promise<void>;
  retry: (messageId: string) => Promise<void>;
  stopGeneration: () => void;
}

function buildConversationTitle(): string {
  const user = useAuthStore.getState().user;
  const who = user?.displayName || user?.username || user?.email || "Guest";
  const when = new Date().toLocaleString();
  return `${who} - ${when}`;
}

function byRecency(a: ConversationSummary, b: ConversationSummary): number {
  const at = a.lastMessageAt ? Date.parse(a.lastMessageAt) : -Infinity;
  const bt = b.lastMessageAt ? Date.parse(b.lastMessageAt) : -Infinity;
  return bt - at;
}

// Pins are localStorage-only (no backend column); every access is try/caught (Safari throws).
const PIN_STORAGE_PREFIX = "ca:pinned:";

function pinStorageKey(): string | null {
  const userId = useAuthStore.getState().user?.userId;
  return userId ? `${PIN_STORAGE_PREFIX}${userId}` : null;
}

function loadPinnedIds(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const key = pinStorageKey();
    if (!key) return [];
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function savePinnedIds(ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    const key = pinStorageKey();
    if (key) window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private mode / quota). Pins stay in memory for this session.
  }
}

// Same storage rules as pins above, for the same reasons.
const PAGE_AWARE_STORAGE_PREFIX = "ca:pageAware:";

function pageAwareStorageKey(): string | null {
  const userId = useAuthStore.getState().user?.userId;
  return userId ? `${PAGE_AWARE_STORAGE_PREFIX}${userId}` : null;
}

/** Defaults to on: anything absent, unreadable or not a boolean means the user has not opted out. */
function loadPageAware(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const key = pageAwareStorageKey();
    if (!key) return true;
    const raw = window.localStorage.getItem(key);
    return raw === null ? true : (JSON.parse(raw) as unknown) !== false;
  } catch {
    return true;
  }
}

function savePageAware(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const key = pageAwareStorageKey();
    if (key) window.localStorage.setItem(key, JSON.stringify(enabled));
  } catch {
    // Storage unavailable. The preference holds for this session and resets on the next.
  }
}

function extractErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { errors?: Array<{ message?: string }>; message?: string } };
    message?: string;
  };
  const envelope = e?.response?.data;
  const first = envelope?.errors?.[0]?.message;
  if (first) return first;
  if (envelope?.message) return envelope.message;
  if (e?.message) return e.message;
  return fallback;
}

function friendlyHistoryError(err: unknown, fallback: string): string {
  const raw = extractErrorMessage(err, fallback);
  return raw === "OFFLINE"
      ? "You appear to be offline. Check your connection and try again."
      : raw;
}

function friendlyAssistantError(raw: string | null | undefined): string {
  const text = (raw ?? "").trim();
  const lower = text.toLowerCase();

  if (/credit balance|billing|quota|insufficient|payment|purchase credit/.test(lower)) {
    return "The Compliance Companion is temporarily unavailable. Please try again later or contact your administrator.";
  }

  if (/rate limit|overloaded|too many requests|\b429\b/.test(lower)) {
    return "The assistant is busy right now. Please wait a moment and try again.";
  }

  if (/unauthorized|forbidden|api key|authentication|\b401\b|\b403\b/.test(lower)) {
    return "The assistant could not reach the service. Please contact your administrator.";
  }

  if (
      !text ||
      /anthropic|invalid_request_error|\{type=|"type"\s*:|bad request|\b[45]\d{2}\b/i.test(text)
  ) {
    return "Something went wrong while generating a response. Please try again.";
  }

  return text;
}


const FOLLOW_UP_LEAD_IN = /^would you like\b.*$/i;
const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s+(.*)$/;
const HR_LINE = /^\s*([-*_])\1{2,}\s*$/;

function stripMarkers(line: string): string {
  return line
      .replace(/^\s*#{1,6}\s*/, "") // heading hashes
      .replace(/[*_`]/g, "") // bold / italic / inline-code markers
      .trim();
}

function joinActions(actions: string[]): string {
  if (actions.length === 1) return actions[0];
  return `${actions.slice(0, -1).join(", ")} or ${actions[actions.length - 1]}`;
}

function collapseFollowUpBlock(content: string, hasFollowUps: boolean): string {
  const lines = content.replace(/\r\n/g, "\n").split("\n");


  let introIdx = -1;
  let listStart = -1;
  for (let i = 0; i < lines.length; i++) {
    if (!FOLLOW_UP_LEAD_IN.test(stripMarkers(lines[i]))) continue;
    let j = i + 1;
    while (j < lines.length && !lines[j].trim()) j++;
    if (j < lines.length && LIST_ITEM.test(lines[j])) {
      introIdx = i;
      listStart = j;
      break;
    }
  }
  if (introIdx === -1) return content;


  let listEnd = listStart;
  const actions: string[] = [];
  while (listEnd < lines.length) {
    const m = lines[listEnd].match(LIST_ITEM);
    if (m) {
      actions.push(stripMarkers(m[1]).replace(/[.:;]+$/, "").trim());
      listEnd++;
    } else if (!lines[listEnd].trim()) {
      listEnd++;
    } else {
      break;
    }
  }

  const beforeLines = lines.slice(0, introIdx);
  while (
      beforeLines.length &&
      (!beforeLines[beforeLines.length - 1].trim() || HR_LINE.test(beforeLines[beforeLines.length - 1]))
      ) {
    beforeLines.pop();
  }
  const before = beforeLines.join("\n");
  const after = lines.slice(listEnd).join("\n").replace(/^\s+/, "");

  let middle = "";
  if (!hasFollowUps) {
    const stem = stripMarkers(lines[introIdx]).replace(/[:?]+\s*$/, "").trim();
    const picks = actions.filter(Boolean).slice(0, 3);

    if (picks.length && /\bto$/i.test(stem)) {
      const sentence = `${stem} ${joinActions(
          picks.map((a) => a.charAt(0).toLowerCase() + a.slice(1)),
      )}?`;
      middle =
          sentence.length <= 200
              ? sentence
              : "Let me know if you would like to explore any of these further.";
    } else {
      middle = "Let me know if you would like to explore any of these further.";
    }
  }

  return [before, middle, after].filter((s) => s.trim().length > 0).join("\n\n").trim();
}

let counter = 0;
const nextId = () => `ca-${++counter}`;

// Drops TOOL/SYSTEM rows: the endpoint returns every row.
function toChatMessages(rows: AssistantConversationMessage[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  let lastPrompt: string | undefined;

  for (const row of rows) {
    if (row.role !== "USER" && row.role !== "ASSISTANT") continue;
    const isAssistant = row.role === "ASSISTANT";
    const raw = row.content ?? "";
    const text = isAssistant ? collapseFollowUpBlock(raw, false) : raw;
    if (!text.trim()) continue;

    out.push({
      id: row.id || nextId(),
      role: isAssistant ? "assistant" : "user",
      text,
      ...(isAssistant && lastPrompt ? { sourcePrompt: lastPrompt } : {}),
      createdAt: Date.parse(row.createdAt ?? "") || Date.now(),
    });
    if (!isAssistant) lastPrompt = raw;
  }

  return out;
}

let pendingConversation: Promise<string | null> | null = null;

let activeStream: AbortController | null = null;


let generation = 0;

/**
 * Invalidation counter for suggestion loads, mirroring {@link generation} above.
 * <p>
 * Navigation can outrun the network: leaving page A while its request is in flight must let B's request
 * win, and A's late response must be discarded rather than painted over B's. A boolean "loading" flag
 * cannot express that — it can only drop the newer request, which is what left the panel showing the
 * previous page's suggestions.
 */
let suggestionsRequest = 0;

/**
 * What the in-flight suggestions request asked for, or null when none is out.
 *
 * The freshness check below compares against the last *settled* answer, which is a different thing from
 * the last question asked. Turning the toggle off and straight back on — or A → B → A faster than the
 * network — leaves state still matching the earlier answer, so the check reports "already loaded",
 * returns without asking, and then the earlier request lands under the new framing. The supersession
 * counter cannot catch that: nothing was superseded, because nothing was sent.
 */
let suggestionsPending: string | null = null;

/** The facts that decide whether two asks are the same question. */
function suggestionsAskKey(
  route: string | null,
  itemId: string | null,
  view: string | null,
  title: string | null,
  variant: number | null,
): string {
  return `${route ?? ""}|${itemId ?? ""}|${view ?? ""}|${title ?? ""}|${variant ?? ""}`;
}

/**
 * How many empty states this user has seen, so the heading and the suggestions can vary between them.
 *
 * Module-level and never reset by a conversation reset, matching {@link generation} and
 * {@link suggestionsRequest}: it is a sequence, not conversation state, so clearing it there would undo
 * the very variation it exists to provide. Logout is the exception — a different user starts the
 * sequence again. Only the fallback for when the number cannot be stored — see {@link loadVisit}.
 */
let greetingVisit = 0;

/** Same storage rules as pins and the page-aware preference above, for the same reasons. */
const VISIT_STORAGE_PREFIX = "ca:visit:";

function visitStorageKey(): string | null {
  const userId = useAuthStore.getState().user?.userId;
  return userId ? `${VISIT_STORAGE_PREFIX}${userId}` : null;
}

/**
 * How many empty states this user has seen, across page loads.
 *
 * Persisted rather than counted per session, because it is the whole of what makes the panel look
 * different the next time. It reaches the backend as `variant`, which keys both the suggestion cache
 * and the window of areas the questions are drawn from — so a counter that restarted at 1 on every
 * refresh asked for the one panel guaranteed to still be cached, and the user met the same four
 * questions every time they reloaded the app.
 */
function loadVisit(): number {
  if (typeof window === "undefined") return greetingVisit;
  try {
    const key = visitStorageKey();
    if (!key) return greetingVisit;
    const raw = Number(window.localStorage.getItem(key));
    // A missing, corrupt or hand-edited value starts the sequence again rather than poisoning it.
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
  } catch {
    return greetingVisit;
  }
}

function saveVisit(visit: number): void {
  greetingVisit = visit;
  if (typeof window === "undefined") return;
  try {
    const key = visitStorageKey();
    if (key) window.localStorage.setItem(key, String(visit));
  } catch {
    // Storage unavailable. The sequence holds for this session and restarts on the next.
  }
}

/** The greeting and visit number for an empty state that is about to appear. */
function nextEmptyState(): { greeting: Greeting; emptyStateVisit: number } {
  const user = useAuthStore.getState().user;
  const firstName = (user?.displayName || user?.username || "").trim().split(/\s+/)[0] || null;
  const visit = loadVisit() + 1;
  saveVisit(visit);
  return { greeting: buildGreeting(firstName, new Date().getHours(), visit), emptyStateVisit: visit };
}

function abortActiveStream() {
  if (activeStream) {
    try {
      activeStream.abort();
    } catch {}
    activeStream = null;
  }
}

export const useComplianceAssistantStore = create<ComplianceAssistantState>((set, get) => {
  // Keeps the history list in sync with the live chat without a refetch: the row for the
  // conversation just written to moves to the top of the list.
  const bumpConversation = (id: string) => {
    const at = new Date().toISOString();
    set((s) => ({
      conversations: s.conversations
          .map((c) => (c.id === id ? { ...c, lastMessageAt: at } : c))
          .sort(byRecency),
    }));
  };

  const ensureConversation = (): Promise<string | null> => {
    const existing = get().conversationId;
    if (existing) return Promise.resolve(existing);
    if (pendingConversation) return pendingConversation;

    set({ conversationLoading: true, conversationError: null });
    pendingConversation = createAssistantConversation(buildConversationTitle())
        .then((conv) => {
          set((s) => ({
            conversationId: conv.id,
            conversationLoading: false,
            conversations: s.conversations.some((c) => c.id === conv.id)
                ? s.conversations
                : [
                  { id: conv.id, title: conv.title, lastMessageAt: null, createdAt: conv.createdAt },
                  ...s.conversations,
                ],
          }));
          return conv.id;
        })
        .catch((err: unknown) => {
          const message = extractErrorMessage(err, "Could not start a conversation.");
          set({ conversationLoading: false, conversationError: message });
          return null;
        })
        .finally(() => {
          pendingConversation = null;
        });

    return pendingConversation;
  };

  const streamPrompt = async (
      prompt: string,
      origin?: AssistantSuggestionOrigin | null,
  ) => {
    const myGen = generation;
    const convId = await ensureConversation();

    if (generation !== myGen) return;
    if (!convId) {
      set((s) => ({
        isThinking: false,
        progress: null,
        messages: [
          ...s.messages,
          {
            id: nextId(),
            role: "assistant",
            error: true,
            text: friendlyAssistantError(
                get().conversationError ?? "Could not start a conversation. Please try again.",
            ),
            sourcePrompt: prompt,
            createdAt: Date.now(),
          },
        ],
      }));
      return;
    }

    bumpConversation(convId);

    abortActiveStream();
    const controller = new AbortController();
    activeStream = controller;

    type Terminal =
        | { kind: "assistant"; id: string; text: string; followUps?: string[]; createdAt: number }
        | { kind: "error"; text: string };
    let terminal: Terminal | null = null;
    let aborted = false;

    // Id of the live-streaming assistant bubble for this turn, created on the first
    // text_delta and grown in place as tokens arrive. Null until the first delta.
    let streamingId: string | null = null;
    // Set by a new iteration: the next delta replaces the superseded draft instead of appending.
    let supersedeOnNextDelta = false;
    const finalizeStreaming = () => {
      if (!streamingId) return;
      const id = streamingId;
      set((s) => ({
        messages: s.messages.map((m) => (m.id === id ? { ...m, streaming: false } : m)),
      }));
    };

    try {
      await streamAssistantMessage(
          convId,
          prompt,
          {
            onIterationStart: ({ iteration }) => {
              // A new iteration supersedes the previous draft. Don't blank the bubble here —
              // that leaves it visibly empty for the whole tool phase. Instead mark it, and
              // let the first delta of the new iteration overwrite the old text in place.
              if (streamingId) supersedeOnNextDelta = true;
              set((s) => ({
                progress: {
                  iteration,
                  lastTool: s.progress?.lastTool,
                  toolHistory: s.progress?.toolHistory ?? [],
                },
              }));
            },
            onTextDelta: ({ text }) => {
              // Render each token the instant it arrives. The first delta replaces the
              // "Thinking…" indicator with a live bubble; later deltas grow it in place.
              if (!text || generation !== myGen || controller.signal.aborted) return;
              if (!streamingId) {
                const id = nextId();
                streamingId = id;
                set((s) => ({
                  isThinking: false,
                  progress: null,
                  messages: [
                    ...s.messages,
                    {
                      id,
                      role: "assistant",
                      text,
                      streaming: true,
                      animate: false,
                      sourcePrompt: prompt,
                      createdAt: Date.now(),
                    },
                  ],
                }));
              } else {
                const id = streamingId;
                const supersede = supersedeOnNextDelta;
                supersedeOnNextDelta = false;
                set((s) => ({
                  messages: s.messages.map((m) =>
                      m.id === id
                        ? { ...m, text: supersede ? text : (m.text ?? "") + text }
                        : m),
                }));
              }
            },
            onToolDispatched: (evt) => {
              set((s) => ({
                progress: {
                  iteration: s.progress?.iteration ?? 1,
                  lastTool: evt.toolName,
                  toolHistory: [...(s.progress?.toolHistory ?? []), evt],
                },
              }));
            },
            onAssistant: ({ message, stopReason, suggestedQuestions }) => {
              const hasFollowUps = !!suggestedQuestions?.length;
              // Suggested Questions are the primary follow-up mechanism; drop the
              // redundant "Would you like me to:" block (or collapse it to one line
              // when no questions were returned) so guidance is never duplicated.
              const body = collapseFollowUpBlock(message?.content ?? "", hasFollowUps);
              const truncated = stopReason === "MAX_TOKENS";
              const text = truncated
                  ? `${body}\n\n*(Reply was cut off because the model hit its output limit. Ask a more specific follow-up to continue.)*`
                  : body;
              terminal = {
                kind: "assistant",
                id: message?.id || nextId(),
                text,
                followUps: hasFollowUps ? suggestedQuestions : undefined,
                createdAt: Date.parse(message?.createdAt ?? "") || Date.now(),
              };
            },
            onError: ({ message }) => {
              terminal = { kind: "error", text: friendlyAssistantError(message) };
            },
            onDone: () => {},
          },
          controller.signal,
          // The page the user is on *now*, not the one the suggestions were loaded for. Those diverge as
          // soon as the user navigates mid-conversation, which is when suggestion loading has stopped
          // running — reading suggestionsRoute here made the assistant answer against a page they left.
          // Read from the store rather than the router: this runs outside the component tree.
          get().currentRoute,
          origin,
          // The record on screen, so "summarise this" resolves to it rather than to the whole page.
          useUiContextStore.getState().selectedItem,
          // And the section it is open in, so on the OSCAL authoring pages "this" means the control the
          // user is reading rather than the catalog behind it — the route says the same thing either way.
          useUiContextStore.getState().view,
      );
    } catch (err: unknown) {
      if (controller.signal.aborted) {
        aborted = true;
      } else {
        terminal = {
          kind: "error",
          text: friendlyAssistantError(
              extractErrorMessage(err, "Something went wrong while generating a response."),
          ),
        };
      }
    }

    if (aborted) {
      // Keep whatever streamed before Stop was pressed, but drop the live caret.
      finalizeStreaming();
      if (activeStream === controller) activeStream = null;
      return;
    }

    const outcome: Terminal =
        (terminal as Terminal | null) ?? {
          kind: "error",
          text: "The assistant did not return a response. Please try again.",
        };

    if (activeStream === controller) activeStream = null;
    if (controller.signal.aborted) {
      finalizeStreaming();
      return;
    }

    const sid = streamingId;
    if (outcome.kind === "assistant") {
      // Replace the live-streamed preview with the authoritative, cleaned answer
      // (suggested-questions block stripped, follow-up block collapsed). Reuse the
      // streaming bubble's id so it updates in place instead of remounting. Only fall
      // back to the client-side typewriter when nothing streamed live (no deltas).
      const finalMessage = {
        id: sid ?? outcome.id,
        role: "assistant" as const,
        text: outcome.text,
        followUps: outcome.followUps,
        sourcePrompt: prompt,
        createdAt: outcome.createdAt,
        streaming: false,
        animate: sid ? false : ENABLE_TYPING_PREVIEW,
      };
      set((s) => {
        const idx = sid ? s.messages.findIndex((m) => m.id === sid) : -1;
        const messages = idx >= 0
            ? s.messages.map((m, i) => (i === idx ? finalMessage : m))
            : [...s.messages, finalMessage];
        return { isThinking: false, progress: null, messages };
      });
    } else {
      // Drop any partial streamed bubble and show the error in its place.
      set((s) => ({
        isThinking: false,
        progress: null,
        messages: [
          ...s.messages.filter((m) => m.id !== sid),
          {
            id: nextId(),
            role: "assistant",
            error: true,
            text: outcome.text,
            sourcePrompt: prompt,
            createdAt: Date.now(),
          },
        ],
      }));
    }
  };

  return {
    open: false,
    hasEverOpened: false,
    minimized: false,
    messages: [],
    isThinking: false,
    progress: null,
    conversationId: null,
    conversationLoading: false,
    conversationError: null,
    historyContext: null,
    conversations: [],
    historyLoading: false,
    historyListError: null,
    pinnedIds: [],
    historyError: null,

    currentRoute: null,
    greeting: null,
    emptyStateVisit: 0,

    suggestions: [],
    suggestionsLoading: false,
    suggestionsSource: null,
    suggestionsUnavailableReason: null,
    suggestionsRoute: null,
    suggestionsItemId: null,
    suggestionsView: null,
    suggestionsPageTitle: null,
    suggestionsVariant: null,
    pageAwareSuggestions: true,

    openPanel: () => {
      // Open into the full workspace by default; the user can collapse it from
      // the header ("Compact") whenever they want the small floating card.
      set({
        open: true,
        hasEverOpened: true,
        minimized: false,
        pinnedIds: loadPinnedIds(),
        pageAwareSuggestions: loadPageAware(),
      });
      // Only on the first open of a session, and only when there is no transcript.
      //
      // This counter is also the suggestion variant, and the variant is part of both the client
      // freshness key and the server's cache key — so advancing it on every open meant every open paid
      // for a fresh round trip to the model, on a page whose questions had not changed. "Show me
      // something different" still happens where the user asks for it: New chat advances it, and so
      // does moving to another page. Reopening the same panel now comes back instantly.
      if (get().messages.length === 0 && get().greeting === null) {
        set(nextEmptyState());
      }
      void ensureConversation();
    },

    hidePanel: () => set({ open: false }),

    closePanel: () => set({ open: false }),

    minimizePanel: () =>
        set((s) => (s.open && !s.minimized ? { minimized: true } : s)),

    maximizePanel: () => set((s) => (s.minimized ? { minimized: false } : s)),

    /* Logout. This store is a module singleton and logout only navigates, so without this the
       next user inherits the previous one's open panel, transcript and conversation id. Resets
       the module-level request state too, or an in-flight answer lands in the new session. */
    resetSession: () => {
      generation++;
      abortActiveStream();
      pendingConversation = null;
      suggestionsRequest++;
      suggestionsPending = null;
      greetingVisit = 0;
      set({
        open: false,
        hasEverOpened: false,
        minimized: false,
        messages: [],
        isThinking: false,
        progress: null,
        conversationId: null,
        conversationLoading: false,
        conversationError: null,
        historyContext: null,
        conversations: [],
        historyLoading: false,
        historyListError: null,
        pinnedIds: [],
        historyError: null,
        currentRoute: null,
        greeting: null,
        emptyStateVisit: 0,
        suggestions: [],
        suggestionsLoading: false,
        suggestionsSource: null,
        suggestionsUnavailableReason: null,
        suggestionsRoute: null,
        suggestionsItemId: null,
        suggestionsView: null,
        suggestionsPageTitle: null,
        suggestionsVariant: null,
        pageAwareSuggestions: true,
      });
    },

    resetConversation: () => {
      generation++;
      abortActiveStream();
      pendingConversation = null;
      set({
        messages: [],
        isThinking: false,
        progress: null,
        conversationId: null,
        conversationLoading: false,
        conversationError: null,
        historyContext: null,
        // The transcript is being cleared, so the empty state is about to appear — always advance.
        ...nextEmptyState(),
        // Clearing the freshness key, not the suggestions: the panel is back to its empty state, so the
        // existing rows stay on screen while ChatPanel's effect refetches for the current page. Blanking
        // them here would flash an empty panel between reset and reload.
        suggestionsRoute: null,
        suggestionsItemId: null,
        suggestionsView: null,
        suggestionsPageTitle: null,
        suggestionsVariant: null,
      });
      void ensureConversation();
    },

    loadConversations: async () => {
      if (get().historyLoading) return;
      set({ historyLoading: true, historyListError: null });
      try {
        const list = await listAssistantConversations();
        set({
          conversations: list
              .map((c) => ({
                id: c.id,
                title: c.title,
                status: c.status,
                lastMessageAt: c.lastMessageAt,
                createdAt: c.createdAt,
              }))
              .sort(byRecency),
          historyLoading: false,
        });
      } catch (err: unknown) {
        const message = friendlyHistoryError(err, "Could not load your conversation history.");
        // A failed REFRESH must not discard a usable list.
        const hasList = get().conversations.length > 0;
        set({
          historyLoading: false,
          historyListError: hasList ? null : message,
          historyError: hasList ? message : null,
        });
      }
    },

    openConversation: async (id) => {
      const summary = get().conversations.find((c) => c.id === id);
      if (!summary) return false;

      let detail;
      try {
        detail = await getAssistantConversation(id);
      } catch (err: unknown) {
        set({ historyError: friendlyHistoryError(err, "Could not open that conversation.") });
        return false;
      }

      get().enterHistoryConversation(
          {
            id: detail.id,
            title: detail.title ?? summary.title,
            lastMessageAt: detail.lastMessageAt ?? summary.lastMessageAt,
          },
          toChatMessages(detail.messages ?? []),
      );
      return true;
    },

    enterHistoryConversation: (context, messages) => {
      // Abort BEFORE swapping, or a still-streaming reply lands in the new conversation.
      generation++;
      abortActiveStream();
      pendingConversation = null;
      set({
        messages: messages.map((m) => ({ ...m, historical: true, animate: false, streaming: false })),
        isThinking: false,
        progress: null,
        conversationId: context.id,
        conversationLoading: false,
        conversationError: null,
        historyContext: context,
      });
    },

    togglePin: (id) => {
      const next = get().pinnedIds.includes(id)
          ? get().pinnedIds.filter((p) => p !== id)
          : [...get().pinnedIds, id];
      set({ pinnedIds: next });
      savePinnedIds(next);
    },

    clearHistoryError: () => set({ historyError: null }),

    deleteConversation: async (id) => {
      const prevConversations = get().conversations;
      const prevPinnedIds = get().pinnedIds;
      const wasActive = get().conversationId === id;

      const pinnedIds = prevPinnedIds.filter((p) => p !== id);
      set({
        conversations: prevConversations.filter((c) => c.id !== id),
        pinnedIds,
        historyError: null,
      });
      savePinnedIds(pinnedIds);

      try {
        await deleteAPI(`${API_URL.AI_COMPANION_CONVERSATIONS}/${id}`);
      } catch (err: unknown) {
        set({
          conversations: prevConversations,
          pinnedIds: prevPinnedIds,
          historyError: friendlyHistoryError(err, "Could not delete the conversation."),
        });
        savePinnedIds(prevPinnedIds);
        return;
      }

      // Never leave the chat pointed at a deleted id.
      if (wasActive) get().resetConversation();
    },

    setCurrentRoute: (route) => {
      // Guarded so navigating does not re-render every subscriber for an unchanged path.
      if (get().currentRoute !== route) {
        set({ currentRoute: route });
      }
    },

    setPageAwareSuggestions: (enabled) => {
      // Deliberately does not fetch or clear. Turning it on makes the next `loadSuggestions` ask about the
      // page the user has since reached; turning it off makes it ask the application-wide question. Either
      // way ChatPanel's effect re-runs and picks it up, which is the one request the change should cost.
      set({ pageAwareSuggestions: enabled });
      savePageAware(enabled);
    },

    loadSuggestions: async (route) => {
      // Suggestions are the empty state. Once a conversation is under way the follow-up chips under the
      // last answer take over, so fetching here would be work nobody sees.
      if (get().messages.length > 0) return;
      // Read here rather than taken as an argument so every trigger — panel open, navigation, selecting a
      // record, moving into a section — sends whatever is current, and no call site can forget to pass it.
      const { selectedItem, view, pageTitle } = useUiContextStore.getState();
      const variant = get().emptyStateVisit;

      // Off is the application-wide question, so the page, the record and the section are all withheld:
      // with no route the backend resolves no screen and answers with one front door per area rather than
      // with anything about where the user happens to be standing.
      //
      // That also holds the rows still, without a guard of its own. All three stay null however far the
      // user navigates, so the freshness check below matches every time and no request goes out — one
      // stable set across pages, which is the point of the state. Toggling flips them, so the check misses
      // and costs exactly one request.
      const pageAware = get().pageAwareSuggestions;
      const askedRoute = pageAware ? route : null;
      const askedItem = pageAware ? selectedItem : null;
      const askedView = pageAware ? view : null;
      const askedTitle = pageAware ? pageTitle : null;
      const itemId = askedItem?.id ?? null;

      // Already asked this exact question — same page, view, record, title *and* rotation — so re-renders
      // and re-opens must not spend a request. Every part of that has to be in the key: the record because
      // selecting a catalog does not change the route, the view because entering its control-groups tree
      // changes neither, the title because the role dashboard's arrives from the auth store and can settle
      // after the first request, and the variant because a new visit on the same page changes nothing
      // else. Leaving any out makes the panel report "already loaded" and never refresh.
      //
      // Measured against the in-flight question when there is one, and against the answer on screen
      // otherwise. Those differ exactly when the user outruns the network, and that is the case where
      // comparing with the wrong one skips a request that was needed.
      const askKey = suggestionsAskKey(askedRoute, itemId, askedView, askedTitle, variant);
      if (
        suggestionsPending !== null
          ? suggestionsPending === askKey
          : askKey ===
              suggestionsAskKey(
                get().suggestionsRoute,
                get().suggestionsItemId,
                get().suggestionsView,
                get().suggestionsPageTitle,
                get().suggestionsVariant,
              ) && get().suggestions.length > 0
      ) {
        return;
      }

      // No "already loading" bail-out. A newer navigation must be able to supersede an in-flight
      // request — dropping it is what left A's suggestions on screen after the user reached B.
      const myRequest = ++suggestionsRequest;
      suggestionsPending = askKey;
      set({ suggestionsLoading: true });

      try {
        const response = await fetchAssistantSuggestions(
          askedRoute,
          undefined,
          askedItem,
          variant,
          askedView,
          askedTitle,
        );
        // Superseded: a later navigation already owns the panel. Returning without clearing the loading
        // flag is deliberate — the winning request still holds it, so the skeleton does not flicker.
        if (myRequest !== suggestionsRequest) return;
        suggestionsPending = null;
        set({
          suggestions: mapSuggestions(response),
          // No source for the local fallback: crediting those clicks to BASE would inflate the backend's
          // numbers with wording it never produced.
          suggestionsSource: isBackendSourced(response) ? response.source : null,
          suggestionsUnavailableReason: response?.available === false
              ? response.unavailableReason
              : null,
          suggestionsRoute: askedRoute,
          suggestionsItemId: itemId,
          suggestionsView: askedView,
          suggestionsPageTitle: askedTitle,
          suggestionsVariant: variant,
          suggestionsLoading: false,
        });
      } catch (error: unknown) {
        // Quiet, not dishonest. The backend being down must not produce a toast or an error state — but
        // it must not produce five plausible questions either, which is what standing in the hardcoded
        // starters here used to do. The panel says it could not load them and shows nothing.
        //
        // The log line stays: route and status only, no prompt, transcript, tenant or record data.
        const status = (error as { response?: { status?: number } })?.response?.status;
        console.warn(
          `[Suggestions] request failed for ${askedRoute ?? "(no page)"}` +
            `${status ? ` (HTTP ${status})` : ""}`,
        );
        // Checked here too: a superseded *failure* must not blank the newer page's suggestions. A slow
        // 500 for A arriving after a good response for B would otherwise wipe it.
        if (myRequest !== suggestionsRequest) return;
        suggestionsPending = null;
        set({
          suggestions: [],
          suggestionsSource: null,
          suggestionsUnavailableReason: "Suggestions could not be loaded just now.",
          suggestionsRoute: askedRoute,
          suggestionsItemId: itemId,
          suggestionsView: askedView,
          suggestionsPageTitle: askedTitle,
          suggestionsVariant: variant,
          suggestionsLoading: false,
        });
      }
    },

    sendMessage: async (text, origin) => {
      const prompt = text.trim();
      if (!prompt || get().isThinking) return;
      set((s) => ({
        messages: [
          ...s.messages,
          { id: nextId(), role: "user", text: prompt, createdAt: Date.now() },
        ],
        isThinking: true,
        progress: null,
      }));
      await streamPrompt(prompt, origin);
    },

    retry: async (messageId) => {
      const msg = get().messages.find((m) => m.id === messageId);
      if (!msg?.sourcePrompt || get().isThinking) return;
      set((s) => ({
        messages: s.messages.filter((m) => m.id !== messageId),
        isThinking: true,
        progress: null,
      }));
      await streamPrompt(msg.sourcePrompt);
    },

    stopGeneration: () => {
      if (!get().isThinking) return;
      generation++;
      abortActiveStream();
      set({ isThinking: false, progress: null });
    },
  };
});

// Wipe the panel on logout. Registered at module scope so it is armed as soon as the store
// loads, covering every logout path (menu, 401 interceptor, AuthGuard, session expiry).
if (typeof onSessionReset === "function") {
  onSessionReset(() => useComplianceAssistantStore.getState().resetSession());
}
